/**
 * What the user asked for: a length, or a finish time.
 *
 * These are two readings of one number — a span that starts now — but which one is
 * *fixed* matters, because time passes while someone is choosing. If they fixed a
 * length, the finish drifts later as they sit deciding. If they fixed a finish, the
 * length shrinks. Storing "25 minutes" and "07:30" as separate settings would make the
 * app quietly break one of the two promises, so only one is ever authoritative and the
 * other is derived on read.
 *
 * Nothing here is persisted. A wall-clock deadline is meaningless tomorrow morning, and
 * restoring one from last week would be worse than useless.
 */

import { signal } from '@preact/signals';

export type TimingIntent =
  | { kind: 'duration'; durationSec: number }
  | { kind: 'deadline'; deadlineMs: number };

export const intent = signal<TimingIntent>({ kind: 'duration', durationSec: 25 * 60 });

/** A ticking clock, so a fixed deadline's derived length counts down on screen. */
export const nowMs = signal(Date.now());

export function startClock(intervalMs = 1000): () => void {
  const id = setInterval(() => { nowMs.value = Date.now(); }, intervalMs);
  return () => clearInterval(id);
}

/**
 * The next time today or tomorrow that reads HH:mm on a local clock.
 *
 * Deliberately built by moving the calendar date rather than adding 24 hours. On the
 * night the clocks change, a day is 23 or 25 hours long, and "tomorrow at 07:30" is a
 * date-and-time question, not an arithmetic one.
 */
export function nextOccurrence(hh: number, mm: number, from = new Date()): Date {
  const at = new Date(from);
  at.setHours(hh, mm, 0, 0);
  if (at.getTime() <= from.getTime()) {
    at.setDate(at.getDate() + 1);
    // setDate can land on a wall-clock time that does not exist (spring forward) or
    // happens twice (fall back); re-stating the time settles it on the real instant.
    at.setHours(hh, mm, 0, 0);
  }
  return at;
}

/** "07:30" as typed by an <input type="time">, or null if it is blank/malformed. */
export function parseClock(value: string): { hh: number; mm: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh > 23 || mm > 59) return null;
  return { hh, mm };
}

export function toClockValue(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Seconds the session would run if it began now. Negative when a deadline has passed. */
export function targetSec(it: TimingIntent, now = Date.now()): number {
  return it.kind === 'duration'
    ? it.durationSec
    : Math.round((it.deadlineMs - now) / 1000);
}

/** When a session started now would finish. */
export function finishMs(it: TimingIntent, now = Date.now()): number {
  return it.kind === 'duration' ? now + it.durationSec * 1000 : it.deadlineMs;
}

/** True when a fixed finish time has already gone by. We never silently roll it over. */
export function isExpired(it: TimingIntent, now = Date.now()): boolean {
  return it.kind === 'deadline' && it.deadlineMs <= now;
}

/** Whether the finish falls on the next calendar day — always shown, never implied. */
export function isTomorrow(ms: number, now = Date.now()): boolean {
  const a = new Date(now);
  const b = new Date(ms);
  return a.getFullYear() !== b.getFullYear()
    || a.getMonth() !== b.getMonth()
    || a.getDate() !== b.getDate();
}

/**
 * `hour: 'numeric'` rather than '2-digit': beside a native time input this reads as a
 * clock, and "01:57 PM" reads as a serial number.
 */
export function formatClock(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "24 min 18 sec" for a live countdown; "25 min" when seconds are not meaningful. */
export function formatExact(sec: number, withSeconds: boolean): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  if (!withSeconds) return `${Math.round(s / 60)} min`;
  return `${m} min ${String(s % 60).padStart(2, '0')} sec`;
}
